import "./NotificationsList.css";

import React, { useState, useCallback, useEffect } from 'react'
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import { Alert, DatePicker, Pagination, Select } from 'antd';
import { TailSpin } from 'react-loader-spinner';
import { Notifications } from '../../api/Notifications';
import { useToasts } from '../../contexts/toasts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import NotificationItem from "../../components/list-items/NotificationItem";
import { cloneDeep } from "lodash";
import { useAuth } from "../../contexts/auth";
import { EventEmitter } from "../../utils/EventEmitter";
import { Link } from "react-router-dom";
import { CustomBootstrapLinkType, notificationTypesOptions } from "../../utils";
import { AppNotification } from "../../models/Notification";


export default function NotificationsList() {
  const [notifications, setNotifications] = useState<AppNotification[]>();
  const [range, setRange] = useState<[any, any] | null>();
  const [notificationType, setNotificationType] = useState<string>();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [total, setTotal] = useState<number>();
  const [error, setError] = useState(false);

  const { showToast } = useToasts();
  const { setNewNotifications } = useAuth();

  useEffect(() => {
    EventEmitter.subscribe("new-notification", (newNotification: AppNotification) => {
      setNotifications(currentNotifications => {
        if (currentPage === 1) {
          const newNotifications = cloneDeep(currentNotifications);
          newNotifications?.unshift(newNotification);
          newNotifications?.pop();
          return newNotifications;
        }
      })
      setTotal(count => count ? count + 1 : undefined);
    });

  }, [currentPage]);

  useEffect(() => {
    return () => EventEmitter.unsubscribe("new-notification");
  }, []);

  useEffect(() => {
    reset();
    // eslint-disable-next-line
  }, [range, itemsPerPage, currentPage, notificationType]);

  const reset = () => {
    setError(false);
    setNotifications(undefined);
    getNotifications();
  }

  const getNotifications = () => {
    const params = new URLSearchParams([['page', `${currentPage}`], ['size', `${itemsPerPage}`]]);
    if (range) {
      params.set("from", range[0].toISOString());
      params.set("to", range[1].toISOString());
    }
    if (notificationType) params.set('type', notificationType);

    Notifications.getNotifications(params)
      .then(
        ({ data }) => {
          setNotifications(data.content);
          setTotal(data.total);
        },
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('notifications-list-error', message, `Notifications Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const readNotification = (id: number) => () => {
    Notifications.readNotification(id)
      .then(
        () => {
          const newNotifications = cloneDeep(notifications);
          const found = newNotifications!.findIndex(n => n.id! === id);
          newNotifications![found].read = true;
          setNotifications(newNotifications);
          setNewNotifications(number => number ? number - 1 : undefined);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('notifications-read-error', message, `Notifications Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const readAllNotifications = () => {
    Notifications.readAllNotifications()
      .then(
        () => {
          setNotifications(nn => nn!.map(n => { n.read = true; return n; }));
          setNewNotifications(0);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('notifications-readall-error', message, `Notifications Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const handleItemsPerPageChange = (page: number, size: number) => {
    changePage(page);
    setItemsPerPage(size);
  }
  const changePage = (page: number) => setCurrentPage(page);

  const renderPagination = useCallback(() => {
    return (
      <div className="text-end">
        <Pagination
          disabled={!notifications}
          total={total}
          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
          pageSize={itemsPerPage}
          current={currentPage}
          onChange={changePage}
          onShowSizeChange={handleItemsPerPageChange}
          simple
        />
      </div>
    )
    // eslint-disable-next-line
  }, [notifications, total, itemsPerPage, currentPage]);

  const renderNotifications = useCallback(() => {
    return (
      <>
        {notifications!.length === 0 && (
          <Alert
            type="info"
            showIcon
            message="No notifications found."
            className="m-2"
          />
        )}

        <div className="notifications-wrapper">
          {notifications!.map(notification => (
            <NotificationItem key={notification.id} notification={notification} onReadNotification={readNotification(notification.id)} />
          ))}
        </div>
      </>
    )
    // eslint-disable-next-line
  }, [notifications]);

  const renderAsyncContent = useCallback(() => {
    if (error) {
      return (
        <Alert
          type="error"
          showIcon
          message="An error occured while retrieving notifications. Try again later"
          className="m-2"
        />
      )
    }

    return notifications
      ? renderNotifications()
      : (
        <div className="d-flex justify-content-center py-5">
          <TailSpin
            height={100}
            width={100}
            color="#677ab1"
            radius="1"
          />
        </div>
      )
  }, [notifications, error, renderNotifications]);

  return (
    <Container className="my-3">
      <Row className="align-items-center mb-3">
        <Col>
          <h5 className="mb-0">Notifications</h5>
        </Col>

        <Col sm={4} className="text-end">
          <Button variant="light" className="border px-3" size="sm" as={Link as unknown as CustomBootstrapLinkType} to="/notifications/preferences">
            <FontAwesomeIcon icon={solid('cog')} />
            <span className="ms-2">Preferences</span>
          </Button>
        </Col>
      </Row>

      <Card>
        <Card.Header className="bg-white d-flex align-items-center">
          <FontAwesomeIcon className="me-2" icon={solid('filter')} />
          <Col md={3}>
            <DatePicker.RangePicker value={range} onChange={setRange} className="w-100" />
          </Col>

          <Col md={3} className="ps-3">
            <Select
              placeholder="Select notification type"
              options={notificationTypesOptions}
              allowClear
              className="w-100"
              value={notificationType}
              onChange={setNotificationType}
            />
          </Col>

          {total !== undefined && total > itemsPerPage && (
            <Col flex="1 0" className="pe-3">{renderPagination()}</Col>
          )}

          <Button variant="light" className="ms-auto border" size="sm" onClick={readAllNotifications}>
            <FontAwesomeIcon icon={solid('check-double')} />
            <span className="ms-2">Mark all as read</span>
          </Button>
        </Card.Header>
        <Card.Body className="p-0">{renderAsyncContent()}</Card.Body>
      </Card>
    </Container>
  )
}
